import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", {
  variants: {
    variant: {
      default: "btn-v-default bg-primary text-primary-foreground border border-transparent shadow-elevation-1 hover:bg-primary/90 hover:shadow-elevation-2 active:bg-primary/85 active:shadow-elevation-1",
      destructive: "btn-v-destructive bg-destructive text-destructive-foreground border border-transparent shadow-elevation-1 hover:bg-destructive/90 hover:shadow-elevation-2 active:bg-destructive/85",
      outline: "btn-v-outline border border-border bg-transparent text-foreground hover:bg-muted hover:border-input active:bg-muted",
      secondary: "btn-v-secondary bg-secondary text-secondary-foreground border border-border hover:bg-muted active:bg-secondary",
      ghost: "btn-v-ghost text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted",
      link: "text-primary underline-offset-4 hover:underline hover:text-primary/80",
      glass: "btn-v-glass bg-card border border-border text-foreground hover:bg-card shadow-elevation-1 hover:shadow-elevation-2 active:bg-card",
      hero: "btn-v-hero bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-elevation-2 hover:shadow-elevation-3 hover:brightness-105 active:brightness-100",
      glow: "btn-v-glow bg-primary text-primary-foreground border border-transparent shadow-[0_0_24px_-8px_hsl(var(--glow-primary)/0.55)] hover:bg-primary/90 hover:shadow-[0_0_34px_-8px_hsl(var(--glow-primary)/0.7)]"


    },
    size: {
      default: "h-10 px-5 py-2",
      sm: "h-9 rounded-lg px-4 text-xs",
      lg: "h-12 rounded-xl px-8 text-base",
      xl: "h-14 rounded-xl px-10 text-lg font-semibold",
      icon: "h-10 w-10"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} {...props} />;
});
Button.displayName = "Button";
export { Button, buttonVariants };